import {
  createViewer,
  GhModelType,
  OptionInstance,
  Option,
  GhModelOptionMap,
  EnvironmentModel,
  State,
} from '@swd_admin/cdc-viewer';
import { OptionInstance as OptionStyleInstance } from 'api/models/OptionInstance';
import ViewerControlButtons from 'components/viewer-control-button/viewer-control-buttons';
import { useSmallScreen } from 'helpers/isSmallScreen';
import React, { useEffect, useState } from 'react';
import ViewerScreenLarge from './viewer-screen-large';
import ViewerScreenSmall from './viewer-screen-small';
import 'viewer/viewerActions'; // viewer api updates
import { useViewerState } from 'state/viewer-state/viewer-state';
import {
  onError,
  onStateChange,
  onSuccess,
  onOptionClick,
  onLoadingProgress,
} from 'viewer/handlers';
import { useAppState } from 'state/appstate/appstate';
import { getUserDefinableInitParams } from 'viewer/viewerActions';
import './viewer.css';
import { motion } from 'framer-motion';
import {
  DefaultBackground,
  OptionInstance as BackendOptionInstance,
} from 'api';
import { ViewerSavingMessage } from 'components/viewer-saving-message';
import { useUiState } from '../../state/ui-state/ui-state';
import * as viewerActions from '../../viewer/viewerActions';

export const EnvironmentTypeAdapter = {
  [DefaultBackground.NONE]: EnvironmentModel.EnvironmentType.Nothing,
  [DefaultBackground.GARDEN]: EnvironmentModel.EnvironmentType.Modern,
  [DefaultBackground.CABIN]: EnvironmentModel.EnvironmentType.Forest,
  [DefaultBackground.POOL]: EnvironmentModel.EnvironmentType.Pool,
  [EnvironmentModel.EnvironmentType.Nothing]: DefaultBackground.NONE,
  [EnvironmentModel.EnvironmentType.Forest]: DefaultBackground.CABIN,
  [EnvironmentModel.EnvironmentType.Modern]: DefaultBackground.GARDEN,
  [EnvironmentModel.EnvironmentType.Pool]: DefaultBackground.POOL,
};

export interface ViewerScreenProps {}
const ViewerScreen: React.FunctionComponent<ViewerScreenProps> = () => {
  const isSmallScreen = useSmallScreen();
  const containerRef =
    React.useRef() as React.MutableRefObject<HTMLInputElement>;
  const { setViewer, viewer, clearSubscriptions, setViewerState } =
    useViewerState((s) => ({
      setViewer: s.setViewer,
      viewer: s.viewer,
      clearSubscriptions: s.clearSubscriptions,
      setViewerState: s.setViewerState,
    }));

  const [bottomSheetHeight, setBottomSheetHeight] = useState(0);
  const appState = useAppState();
  const { setViewerFirstLoading } = useUiState();

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const create = async () => {
      const defaultConfig = appState.styleProduct.style?.defaultConfiguration;
      const { style, product } = appState.styleProduct;
      const { shapeDiverIdToDbId, dbIdToShapeDiverId } = appState.dicts;

      const materials: Array<any> = product!.materials
        .filter((m) => m.ghMaterialJson)
        .map((m) => ({ ...JSON.parse(m.ghMaterialJson), id: m.orderCode }));

      const baseMaterials: Array<any> = product!
        .baseMaterials!.filter((m) => m.ghMaterialJson)
        .map((m) => JSON.parse(m.ghMaterialJson));
      const allMaterials = [...materials, ...baseMaterials];

      const viewerOptionTypeIds = ['roof', 'doors', 'windows'];
      const isViewerOption = (o: OptionStyleInstance) => {
        if (!o.ghModelJson) return false;
        // Material upgrade options (Urban Pavilion) have ghModelJson but should
        // not become viewer instances — they use updateRenderParameters instead.
        const apiOption = style!.options.find(
          (opt) => opt.id === o.definitionId
        );
        if (!apiOption) return true; // unknown options pass through as before
        return viewerOptionTypeIds.includes(apiOption.optionTypeId);
      };
      const correctStyleInstance = ({
        definitionId,
        optionId,
        ...i
      }: OptionInstance & { definitionId?: string }) => ({
        ...i,
        optionId: definitionId || optionId,
      });
      const correctDefaultStyleOptionInstance = (
        option: BackendOptionInstance & { features?: BackendOptionInstance[] }
      ) => {
        const { id, parentId, localPosition, definitionId } = option;
        const shapeDiverId = dbIdToShapeDiverId![definitionId];
        if (!shapeDiverId) {
          console.warn(
            `options with db id ${definitionId} doesn't have shapeDiver id (probably because it wasn't received from the backend)`
          );
        }
        option?.features?.forEach((feature: Option) => {
          feature.id = shapeDiverIdToDbId![feature.id];
        });
        return {
          id,
          parentId,
          localPosition,
          optionId: shapeDiverId,
        };
      };

      const defaultInstances: OptionInstance[] =
        defaultConfig?.style?.options
          ?.filter(isViewerOption)
          ?.map(correctDefaultStyleOptionInstance)
          .filter(({ optionId }) => !!optionId) ?? [];

      const ghModelJson = JSON.parse(defaultConfig?.style.ghModelJson!);

      ghModelJson.instances = [
        ...ghModelJson.instances.map(correctStyleInstance),
        ...defaultInstances,
      ];

      ghModelJson.renderParameters = {
        style_wall_materialId: 'white',
        style_trimming_materialId: 'white',
        style_columns_materialId: 'white',
        style_roof_materialId: 'white',
        ...ghModelJson.renderParameters,
        ...getUserDefinableInitParams(),
      };

      ghModelJson.defaultInstanceIds = ghModelJson.instances.map(
        (i: OptionInstance) => i.id
      );

      const mapToOption = (v: Option) => JSON.parse(v.ghModelJson);

      const options: GhModelOptionMap = {
        [GhModelType.Style]: [
          ...ghModelJson.localOptions,
          ...style!.options
            .filter((v) => v.optionTypeId === 'roof')
            .map(mapToOption),
        ],
        [GhModelType.Doors]: style!.options
          .filter(
            (v) => v.optionTypeId === 'doors' && v.optionFolderId !== '26'
          )
          .map(mapToOption),
        [GhModelType.SlidingDoors]: style!.options
          .filter(
            (v) => v.optionTypeId === 'doors' && v.optionFolderId === '26'
          )
          .map(mapToOption),
        [GhModelType.Windows]: style!.options
          .filter((v) => v.optionTypeId === 'windows')
          .map(mapToOption),
        [GhModelType.Features]: style!.features.map(mapToOption),
      };

      const ticketIdOverwrites = {
        ...JSON.parse(
          window.localStorage.getItem('ticket-id-overwrites') || '{}'
        ),
      };

      const environmentType =
        (style?.defaultConfiguration?.style.defaultBackground &&
          EnvironmentTypeAdapter[
            style?.defaultConfiguration.style.defaultBackground
          ]) ||
        EnvironmentModel.EnvironmentType.Forest;
      appState.setDefaultBackground(EnvironmentTypeAdapter[environmentType]);

      (window as any).overwriteTickets = (obj: Record<string, unknown>) => {
        window.localStorage.setItem(
          'ticket-id-overwrites',
          JSON.stringify(obj)
        );
        window.location.reload();
      };

      (window as any).clearOverwriteTickets = () => {
        window.localStorage.removeItem('ticket-id-overwrites');
      };

      setViewerFirstLoading(true);

      const v = await createViewer(
        containerRef.current,
        {
          dimensionLinesEnabled: false,
          wallPaintColor: null,
          trimPaintColor: null,
          environmentType,
          focusType: null,
          ghModelOptionMap: options,
          ticketIdOverwrites,
          materials: allMaterials!,
          style: ghModelJson,
          doorHeightToWallHeightMap: style!
            .doorHeightToWallHeightMap as State['doorHeightToWallHeightMap'],
        },
        {
          onError,
          onSuccess,
          onStateChange,
          onOptionClick,
          onLoadingProgress,
        }
      );
      if (v) {
        setViewer(v);
        setViewerState(v.initialState);
        viewerActions.setConcretePadOption(!!appState.uState?.hasConcretePad);

        // Apply render parameters for hybrid BOM-only options (Urban Pavilion material_upgrades)
        appState.uState?.bomOnlyOptions.forEach((bomOpt) => {
          const configOpt = defaultConfig?.style?.options?.find(
            (o) => o.definitionId === bomOpt.definitionId
          );
          if (configOpt?.ghModelJson) {
            const parsed = JSON.parse(configOpt.ghModelJson);
            if (parsed.updateParameters) {
              viewerActions.updateRenderParameters(parsed.updateParameters);
            }
          }
        });

        await v.resetCamera();
        setTimeout(() => {
          setViewerFirstLoading(false);
        }, 2000);
      }
    };
    !viewer && create();
  }, [containerRef, viewer]);

  useEffect(() => {
    return () => {
      clearSubscriptions();
      viewer?.teardown();
      setViewer(undefined);
    };
  }, []);

  const ViewerScreen = isSmallScreen ? ViewerScreenSmall : ViewerScreenLarge;

  return (
    <ViewerScreen
      bottomSheetHeightChange={(height) => {
        setBottomSheetHeight(height);
        viewer?.render();
      }}
    >
      <ViewerSavingMessage />
      <div style={{ position: 'relative', height: '100%' }}>
        <div
          ref={containerRef}
          style={{
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: `calc(100% - ${bottomSheetHeight}px)`,
            overflow: 'hidden',
          }}
        />
        {
          <motion.div
            animate={{
              opacity: viewer ? 1 : 0.5,
              pointerEvents: viewer ? 'auto' : 'none',
            }}
          >
            <ViewerControlButtons bottomSheetHeight={bottomSheetHeight} />
          </motion.div>
        }
      </div>
    </ViewerScreen>
  );
};

export default ViewerScreen;
